import { type HydratedChatbotAsset } from "@zapier/bff";
import { useSupportedAssets } from "@zapier/client";
import { BetaColumnHeader, SortDescriptor } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { Table, useRouterParams, type BulkSelectAction } from "@zapier/shared";
import { compact } from "@zapier/utils";
import { useMemo } from "react";
import { useChatbotModalStates } from "../useChatbotModalStates";
import { HydratedChatbotWithFolder } from "../useChatbotsList";
import { ChatbotModals } from "./ChatbotModals";
import ChatbotRow from "./ChatbotRow";
import styles from "./ChatbotsTable.module.css";
import { EmptyChatbotsTable } from "./EmptyChatbotsTable";
import { useShowEmptyChatbotsTable } from "./useShowEmptyChatbotsTable";
import { ConversationsColumnLabel } from "./ConversationsColumnLabel";

interface ChatbotsTableProps {
  bulkSelectActions?: BulkSelectAction[];
  chatbots: HydratedChatbotWithFolder[];
  isLoading: boolean;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  onCopyToAnotherAccount?: (asset: HydratedChatbotAsset) => void;
}

const ChatbotsTable = ({
  bulkSelectActions,
  chatbots,
  isLoading,
  onSortChange,
  sortDescriptor,
  onCopyToAnotherAccount,
}: ChatbotsTableProps) => {
  const t = useTranslations("chatbots-table");
  const tAssetTypes = useTranslations("asset-types");
  const { currentChatbot, currentModal, setCurrentChatbot, setCurrentModal } =
    useChatbotModalStates();
  const { search, owner, shared } = useRouterParams();
  const isFiltered = Boolean(search || owner);
  const { isMPFEnabled } = useSupportedAssets();

  const { showEmptyState, emptyType } = useShowEmptyChatbotsTable({
    chatbots,
    isLoading,
    isFiltered,
    shared,
  });

  const columns = useMemo(() => {
    return compact<BetaColumnHeader>([
      {
        label: <ConversationsColumnLabel />,
        allowsSorting: false,
        className: styles.recentConversationsColumn,
      },
      {
        label: t("columns.model"),
        allowsSorting: false,
        className: styles.modelColumn,
      },
      isMPFEnabled && {
        label: t("columns.location"),
        allowsSorting: false,
        className: styles.locationColumn,
      },
      {
        sortKey: "updatedAt",
        label: t("columns.last-modified"),
        allowsSorting: true,
        className: styles.lastModifiedColumn,
      },
    ]);
  }, [isMPFEnabled, t]);

  if (showEmptyState && emptyType) {
    return <EmptyChatbotsTable tableType={emptyType} />;
  }

  return (
    <>
      <Table
        allowSortByTitle={true}
        ariaLabel={t("aria-label")}
        bulkActionsAssetLabel={tAssetTypes("plural.chatbot")}
        bulkSelectActions={bulkSelectActions}
        columns={columns}
        isLoading={isLoading}
        onSortChange={onSortChange}
        selectableEntities={chatbots}
        showCheckboxes={!!bulkSelectActions}
        showOwner={true}
        // No share action on Chatbots rows (no onClickShare), so don't reserve
        // share-button width — keeps the Options column consistent with Zaps.
        showShareButton={false}
        sortDescriptor={sortDescriptor}
        titleSortKey="name"
        className={styles.chatbotsTable}
      >
        {chatbots?.map((chatbot) => (
          <ChatbotRow
            allowsSelection={!!bulkSelectActions}
            key={chatbot.id}
            chatbot={chatbot}
            searchQuery={search}
            setCurrentChatbot={setCurrentChatbot}
            setCurrentModal={setCurrentModal}
            showLocation={isMPFEnabled}
            onCopyToAnotherAccount={onCopyToAnotherAccount}
          />
        ))}
      </Table>

      <ChatbotModals
        currentModal={currentModal}
        currentChatbot={currentChatbot}
        setCurrentChatbot={setCurrentChatbot}
        setCurrentModal={setCurrentModal}
      />
    </>
  );
};

export default ChatbotsTable;
